import React, { useState, useRef, useEffect, Fragment, useCallback } from "react";
import OverviewSectionWrapper from "../section/OverviewSectionWrapper";
import SpendDropdown from "../components/dropdowns/SpendDropdown";
import { Cell, Pie, PieChart } from "recharts";
import {
  IconPokerChip,
  IconPhoto,
  IconMail,
  IconMovie,
  IconChartCircles,
  IconFishHook,
  IconUser,
} from "@tabler/icons-react";
import { Separator } from "@/components/ui/separator";
import MetricCard from "../cards/MetricCard";
import { AccountOverviewData, Column, DataGraphResponse, Status } from "../../../types";
import WaveLoader from "@/components/common/loaders/WaveLoader";
import NothingToShow from "@/components/common/emptyStates/NothingToShow";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import ErrorState from "@/components/common/errorStates/ErrorState";
import { RemixiconComponentType } from "@remixicon/react";
import { SectionConfigWithMetadata } from "@/types";
import SparkGraph from "../components/SparkGraph";
import { GraphDialog } from "../components/GraphDialog";
import axios from "axios";
import { useParams } from "react-router-dom";
import { getDataGraph } from "@/screens/creatives/apiService";
import { getOptimumInterval } from "@/utils/dateUtils";
import { cache } from "@/services/cache";
import { useGlobalStore } from "@/GlobalStore";

interface Props {
  data?: AccountOverviewData;
  status: Status;
  loading: boolean;
  error?: { hasError: boolean; message?: string };
  defaultMetricId?: string;
  onSetDefaultMetric: (metricId: string) => void;
  onRetry?: () => void;
  title?: string;
  icon?: RemixiconComponentType;
  sectionId: string;
  configWithReportMetadata?: SectionConfigWithMetadata;
}

const AccountOverview: React.FC<Props> = ({
  data,
  loading = true,
  error,
  defaultMetricId,
  onSetDefaultMetric,
  onRetry,
  title = "Account Overview",
  icon = IconPokerChip,
  sectionId,
  configWithReportMetadata,
}) => {
  const [selectedSpend, setSelectedSpend] = useState<string>("");
  const [selectedMetricId, setSelectedMetricId] = useState<string>(defaultMetricId || "");

  // Set default selectedSpend when adDistribution data is available
  useEffect(() => {
    if (data?.ad_distribution && data.ad_distribution.length > 0 && !selectedSpend) {
      setSelectedSpend(data.ad_distribution[0]?.key);
    }
  }, [data?.ad_distribution, selectedSpend]);

  useEffect(() => {
    if (defaultMetricId) {
      setSelectedMetricId(defaultMetricId);
    }
  }, [defaultMetricId]);

  const handleMetricChange = (metric: string) => {
    setSelectedMetricId(metric);
  };

  const handleSpendChange = (spend: string) => {
    setSelectedSpend(spend);
  };

  // Function to get appropriate icon based on hook key
  const getHookIcon = (hookKey: string) => {
    const iconMap: Record<
      string,
      React.ComponentType<{ size?: number | string; className?: string }>
    > = {
      hook: IconFishHook,
      message: IconMail,
      creative: IconPhoto,
      audience: IconChartCircles,
      platform: IconPokerChip,
      default: IconChartCircles,
    };
    return iconMap[hookKey] || iconMap.default;
  };

  const [openGraphDialog, setOpenGraphDialog] = useState<boolean>(false);
  const [defaultSelectedMetric, setDefaultSelectedMetric] = useState<string | null>(null);

  const isMounted = useRef(true);
  // Single timeout reference

  const [pollingTimeout, setPollingTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);

  // graph data
  const { accountId } = useParams();
  const [loadingGraph, setLoadingGraph] = useState<boolean>(false);
  const [graphData, setGraphData] = useState<DataGraphResponse>();

  // Cleanup effect
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, [pollingTimeout]);

  // Clear polling timeout helper
  const clearPollingTimeout = useCallback(() => {
    if (pollingTimeout) {
      clearTimeout(pollingTimeout);
      setPollingTimeout(null);
    }
  }, [pollingTimeout]);

  // fetching graph data only for spark graph
  useEffect(() => {
    const cancelToken = axios.CancelToken.source();

    const fetchGraphData = async (isPolling?: boolean) => {
      if (!accountId) return;
      if (!isPolling) {
        setLoadingGraph(true);
      }

      const interval = getOptimumInterval({
        start_date: configWithReportMetadata?.date?.start_date ?? "",
        end_date: configWithReportMetadata?.date?.end_date ?? "",
      });

      const metadata = {
        ...(configWithReportMetadata ?? {}),
        dropdowns: {
          ...configWithReportMetadata?.dropdowns,
          group_by: "aggregated",
        },
        column_choices: {
          // fetching all the metrics initially along with new_ads and active_ads
          metric: [
            ...(dataConfigurations?.column_choices
              ?.find((c) => c.key === "metric")
              ?.options.map((m) => m.key) ?? []),
            "new_ads",
            "active_ads",
          ],
        },
      };

      // Generate cache key
      const cacheKey = cache.generateKey(
        `/creative/${accountId}/data-graph/${sectionId}/`,
        {
          interval,
          metadata,
        },
        accountId,
      );

      // Try to get from cache first (unless polling)
      let hasCachedData = false;
      if (!isPolling) {
        try {
          const cachedData = await cache.get<DataGraphResponse>(cacheKey);
          if (cachedData) {
            setGraphData(cachedData);
            setLoadingGraph(false);
            hasCachedData = true;
          }
        } catch (error) {
          console.warn("Failed to get data from cache:", error);
        }
      }

      try {
        const response = await getDataGraph(
          accountId,
          sectionId,
          interval,
          metadata,
          cancelToken.token,
        );

        // If component unmounted during request, don't proceed
        if (!isMounted.current) return;

        // Cache the response (only if not pending)
        if (response?.status !== "pending") {
          try {
            await cache.set(cacheKey, response, 24 * 60 * 60 * 1000); // 24 hours TTL
          } catch (error) {
            console.warn("Failed to cache data:", error);
          }
        }

        // Check response status and set up polling if needed
        if (response?.status === "pending") {
          setGraphData(response);
          // Set up next polling iteration
          const timeoutId = setTimeout(() => {
            void fetchGraphData(true);
          }, 5000);

          setPollingTimeout(timeoutId);
        } else {
          // Only update UI with fresh data if we didn't have cached data
          // This prevents flicker when cached data is already showing
          if (!hasCachedData) {
            setGraphData(response);
            setLoadingGraph(false);
          }
          clearPollingTimeout();
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Error fetching graph data:", error);
          setLoadingGraph(false);
          clearPollingTimeout();
        }
      }
    };
    void fetchGraphData();

    return () => {
      cancelToken.cancel();
      clearPollingTimeout();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    configWithReportMetadata?.report_filters,
    configWithReportMetadata?.date?.start_date,
    configWithReportMetadata?.date?.end_date,
  ]);

  const dataConfigurations = useGlobalStore((s) => s.dataConfigurations);
  const metricsColumKeys = data?.overview_metrics_data?.creative_metric_columns.map(
    (m) => m.key,
  );

  const metricOptions =
    dataConfigurations?.column_choices
      ?.find((c) => c.key === "metric")
      ?.options.filter((o) => metricsColumKeys?.includes(o.key)) ?? [];

  const graphMetricOptions = Array.from(
    new Map([
      ...(data?.overview_metrics_data?.columns ?? []).map(
        (item) => [item.key, item] as [string, Column],
      ),
      ...metricOptions.map((item) => [item.key, item] as [string, Column]),
    ]).values(),
  );

  return (
    <OverviewSectionWrapper
      title={title}
      titleIcon={icon}
      showInfoIcon={true}
      tooltipContent="A snapshot of your ad account’s key performance, see trends, spend, and results at a glance."
    >
      {loading ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <WaveLoader />
        </div>
      ) : error?.hasError ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <ErrorState onRetry={onRetry} />
        </div>
      ) : !data ? (
        <div className="w-full h-[300px] flex items-center justify-center">
          <NothingToShow />
        </div>
      ) : (
        <div className="flex flex-col gap-[8px]">
          {/* First Row - Metrics Cards */}
          <div>
            <div className="p-[1px] flex w-max rounded-[12px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray">
              {data?.overview_metrics_data.columns.map((column) => {
                const valueKey = column.is_kpi ? selectedMetricId || "" : column.key;
                const metricColumn = column.is_kpi
                  ? (data?.overview_metrics_data.creative_metric_columns?.find(
                      (col) => col.key === valueKey,
                    ) ?? column)
                  : column;
                const changeKey = `${valueKey}_percent_change`;
                const changeValue = data.overview_metrics_data.data[changeKey];

                return (
                  <MetricCard
                    key={metricColumn.key}
                    title={!column.is_kpi ? column.label : undefined}
                    value={data.overview_metrics_data.data[valueKey]}
                    change={changeValue}
                    hasDropdown={column.is_kpi}
                    prefix={metricColumn.prefix ?? ""}
                    suffix={metricColumn.suffix ?? ""}
                    selectedMetric={selectedMetricId}
                    onMetricChange={handleMetricChange}
                    className={"min-w-[200px]"}
                    metricOptions={metricOptions}
                    defaultMetric={defaultMetricId}
                    onSetDefault={onSetDefaultMetric}
                    sparkGraph={
                      <SparkGraph
                        onClick={() => {
                          setOpenGraphDialog(true);
                          setDefaultSelectedMetric(metricColumn.key);
                        }}
                        graphData={graphData}
                        loading={loadingGraph}
                        metricKey={valueKey}
                        change={changeValue}
                        className="h-8 w-20"
                        sectionId={sectionId}
                      />
                    }
                  />
                );
              })}
            </div>
          </div>

          {/* Second Row - 3 Sections */}
          <div className="h-[267px] flex gap-[8px]">
            <div className="w-[233px] h-full">
              <ActiveAdsSection
                selectedSpend={selectedSpend}
                onSpendChange={handleSpendChange}
                adDistribution={data?.ad_distribution}
              />
            </div>
            {data?.hooks?.map((hook) => (
              <div key={hook.key} className="flex-1 h-full">
                <PerformanceSection
                  title={hook.label}
                  icon={getHookIcon(hook.key)}
                  status={hook.status}
                  items={
                    hook.data?.map((item) => ({
                      text: item.label,
                      metric: `${item.value}${item.suffix || ""}`,
                      subMetric: item.percent ? `${item.percent}%` : undefined,
                      info: item.info,
                    })) || []
                  }
                  metricClassName={
                    hook.key === "hook" ? "text-brand-primary-600" : undefined
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {graphData && (
        <GraphDialog
          open={openGraphDialog}
          onOpenChange={setOpenGraphDialog}
          metrics={graphMetricOptions}
          configWithReportMetadata={configWithReportMetadata ?? {}}
          defaultSelectedMetric={defaultSelectedMetric}
          sectionId={sectionId}
          initialGraphData={graphData}
          currentPeriodColor={(() => {
            const change =
              data?.overview_metrics_data?.data?.[
                `${defaultSelectedMetric}_percent_change`
              ];
            if (typeof change === "number") return change > 0 ? "emerald" : "red";
            else return "emerald";
          })()}
        />
      )}
    </OverviewSectionWrapper>
  );
};

// Active Ads Section Component
interface ActiveAdsSectionProps {
  selectedSpend: string;
  onSpendChange: (spend: string) => void;
  adDistribution?: AccountOverviewData["ad_distribution"];
}

const ActiveAdsSection: React.FC<ActiveAdsSectionProps> = ({
  selectedSpend,
  onSpendChange,
  adDistribution,
}) => {
  // Chart data - dynamically generate from adDistribution data based on selectedSpend
  const selectedDistribution = adDistribution?.find((dist) => dist.key === selectedSpend);

  // Don't render anything if no data is available
  if (
    !selectedDistribution ||
    !selectedDistribution.data ||
    selectedDistribution.data.length === 0
  ) {
    return (
      <div className="h-full w-full bg-white rounded-[12px] p-[8px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray">
        <div className="flex items-center justify-between">
          <div className="pl-[8px]">
            <h4 className="text-[12px] font-medium text-brand-primary-800">Active Ads</h4>
          </div>
        </div>

        <div className="flex items-center justify-center text-center gap-[18px]">
          <NothingToShow />
        </div>
      </div>
    );
  }

  const chartData = selectedDistribution.data.map((item, index) => {
    // Define colors for different media types
    const colors = ["#EC4899", "#10B981", "#6B7280", "#F59E0B", "#8B5CF6"];
    return {
      name: item.label,
      value: item.value,
      percentage: item.percent,
      color: colors[index % colors.length],
      prefix: item.prefix,
      suffix: item.suffix,
    };
  });

  // Calculate total spend dynamically from selected distribution data
  const totalSpend = selectedDistribution.total;

  return (
    <div className="w-full h-full bg-white rounded-[12px] p-[8px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray">
      <div className="flex items-center justify-between mb-[37px]">
        <div className="pl-[8px]">
          <h4 className="text-[12px] font-medium text-brand-primary-800">Active Ads</h4>
        </div>
        <SpendDropdown
          selected={selectedSpend}
          onChange={onSpendChange}
          className="text-[12px]"
          options={
            adDistribution?.map((dist) => ({
              key: dist.key,
              value: dist.key,
              label: dist.label,
            })) || []
          }
        />
      </div>

      <div className="flex flex-col items-center gap-[18px]">
        {/* Recharts Donut Chart */}
        <div className="relative">
          <PieChart width={168} height={84}>
            <defs>
              {/* Gradient for Images (Pink) - Based on paint0_linear_36773_1250 */}
              <linearGradient
                id="gradientImages"
                x1="84"
                y1="0"
                x2="7.5"
                y2="84"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#FF53A0" />
                <stop offset="1" stopColor="#FF9DC9" />
              </linearGradient>
              {/* Gradient for Videos (Green) - Based on paint1_linear_36773_1250 */}
              <linearGradient
                id="gradientVideos"
                x1="84"
                y1="0"
                x2="160"
                y2="67"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#0EC28C" />
                <stop offset="1" stopColor="#01A070" />
              </linearGradient>
              {/* Gradient for Others (Gray) - Custom similar gradient */}
              <linearGradient
                id="gradientOthers"
                x1="84"
                y1="0"
                x2="7.5"
                y2="84"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#F6F6F6" />
                <stop offset="1" stopColor="#F6F6F6" />
              </linearGradient>
            </defs>
            <Pie
              data={chartData}
              cx={80}
              cy={84}
              startAngle={180}
              endAngle={0}
              innerRadius={65}
              outerRadius={84}
              paddingAngle={1}
              dataKey="value"
            >
              {chartData.map((entry) => {
                const gradientId = `gradient${entry.name}`;
                return <Cell key={`cell-${entry.name}`} fill={`url(#${gradientId})`} />;
              })}
            </Pie>
          </PieChart>
          {/* Center label */}
          <div className="absolute inset-0 flex flex-col items-center justify-end">
            <span className="text-[14px] font-normal text-brand-primary-800">
              {selectedDistribution.prefix}
              {totalSpend.toLocaleString()}
              {selectedDistribution.suffix}
            </span>
            <span className="text-[10px] text-brand-primary-800">
              Total {selectedDistribution.label}{" "}
            </span>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-col gap-[6px] w-full px-4">
          {chartData.map((item) => (
            <div
              key={item.name}
              className="w-full flex gap-4 items-center justify-between"
            >
              <div className="flex items-center gap-[8px]">
                {/* Dynamic icon mapping based on item name */}
                {item.name.toLowerCase().includes("image") && (
                  <IconPhoto size={14} className="text-[#FF4699]" />
                )}
                {item.name.toLowerCase().includes("video") && (
                  <IconMovie size={14} className="text-[#018C62]" />
                )}
                {!item.name.toLowerCase().includes("image") &&
                  !item.name.toLowerCase().includes("video") && (
                    <IconChartCircles size={14} className="text-brand-primary-400" />
                  )}
                <span className="text-[12px] text-brand-primary-800">{item.name}</span>
              </div>
              <div className="flex-1 flex items-center gap-[8px] justify-end overflow-hidden">
                <span className="text-[12px] font-medium text-brand-primary-800 truncate">
                  {item.prefix}
                  {item.value.toLocaleString()}
                  {item.suffix}
                </span>
                <span className="text-[12px] text-brand-primary-600">
                  {item.percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// DRY Performance Section Component
interface PerformanceItem {
  text: string;
  metric: string;
  subMetric?: string;
  info?: string;
}

interface PerformanceSectionProps {
  title: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
  items: PerformanceItem[];
  status?: Status;
  metricClassName?: string;
}

const PerformanceSection: React.FC<PerformanceSectionProps> = ({
  title,
  icon: Icon,
  items,
  status,
  metricClassName,
}) => {
  const [isScrollable, setIsScrollable] = useState(false);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const optionsContainerRef = useRef<HTMLDivElement>(null);

  // Check if content is scrollable
  useEffect(() => {
    const checkScrollable = () => {
      if (optionsContainerRef.current) {
        const { scrollHeight, clientHeight } = optionsContainerRef.current;
        setIsScrollable(scrollHeight > clientHeight);
      }
    };

    // Check initially and on items change
    checkScrollable();

    // Add resize observer to handle dynamic changes
    const resizeObserver = new ResizeObserver(checkScrollable);
    if (optionsContainerRef.current) {
      resizeObserver.observe(optionsContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [items]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setIsScrolledToBottom(
      e.currentTarget.scrollTop + e.currentTarget.clientHeight >=
        e.currentTarget.scrollHeight - 10,
    );
  };

  return (
    <div className="w-full h-full bg-white rounded-[12px] flex flex-col items-start gap-[18px] px-[6px] pt-[12px] pb-[6px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray">
      {/* Title */}
      <div className="flex items-center gap-[8px] pl-1">
        <Icon size={14} className="text-brand-primary-600" />
        <h4 className="text-[11px] font-medium text-brand-primary-800">{title}</h4>
      </div>
      {/* Body */}
      <div className="relative w-full flex-1 overflow-hidden">
        {status === "pending" ? (
          <div className="w-full h-full flex items-center justify-center">
            <WaveLoader className="w-[120px]" />
          </div>
        ) : items.length === 0 ? (
          <div className="w-full h-full flex items-center justify-center">
            <NothingToShow />
          </div>
        ) : (
          <div
            ref={optionsContainerRef}
            onScroll={handleScroll}
            className="w-full h-full overflow-y-auto flex flex-col items-start gap-[12px] px-[8px] py-[12px] rounded-[8px] squircle squircle-radius-[8px] squircle-outline-[1px] squircle-outline-color-border-gray"
          >
            {items.map((item, index) => (
              <Fragment key={index}>
                <div
                  key={index}
                  className="w-full flex items-center justify-between gap-[8px]"
                >
                  <p className="text-[11px] text-brand-primary-800 flex-1 leading-[14px]">
                    {item.text}
                  </p>
                  <div className="flex items-center gap-[8px] flex-shrink-0">
                    {item.metric && (
                      <span
                        className={cn(
                          "text-[11px] text-brand-primary-800 whitespace-nowrap",
                          metricClassName,
                        )}
                      >
                        {item.metric}
                      </span>
                    )}
                    {item.subMetric && (
                      <span className="text-[11px] text-brand-primary-600 whitespace-nowrap">
                        {item.subMetric}
                      </span>
                    )}
                    {item.info && (
                      <Tooltip>
                        <TooltipTrigger>
                          <IconUser size={14} className="text-brand-primary-600" />
                        </TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          align="start"
                          sideOffset={10}
                          alignOffset={14}
                        >
                          {item.info}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
                {index < items.length - 1 && (
                  <Separator className="w-full bg-border-gray/60" />
                )}
              </Fragment>
            ))}
          </div>
        )}
        {isScrollable && !isScrolledToBottom && status !== "pending" && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[46px] pointer-events-none"
            style={{
              background:
                "linear-gradient(0deg, #FFF 0%, rgba(255, 255, 255, 0.00) 100%)",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AccountOverview;
